import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {Location} from '@angular/common'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PageHelperService } from '../helpers/page-helper.service';
import { BlackboxPositionDto, JourneyItemDto, MovementService } from 'src/shared/service-proxies/service-proxies';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import * as L from 'leaflet';
import { LocationClassNameHelper } from '../enums/LocationTypeNames';
import { GeoHelper } from '../helpers/geo';
import { GoogleSnappedRoadItem, GoogleSnappedRoadResult, RoadsService } from '../services/roads.service';
import { DateTimeHelper } from '../helpers/datetime';
import { CustomerSettings } from '../services/customer-settings-service';

export interface JobItemModel extends JourneyItemDto {
  journeyFormatted?: string;
  arrivedAtFormatted?: string;
  departedAtFormatted?: string;
  mileageFormatted?: string;
  mileageVarianceFormatted?: string;
}

@Component({
    selector: 'app-route-map',
    templateUrl: './route-map.component.html',
    styleUrls: ['./route-map.component.scss'],
    standalone: false
})
export class RouteMapComponent extends PagebaseComponent implements OnInit {
  
  
  @ViewChild('mapdiv') 
  mapDiv: ElementRef;
  locationDataLoaded: boolean = false;  
  job: JourneyItemDto;
  jobModel: JobItemModel;
  routePositions: BlackboxPositionDto[];
  
  // Private variables
  private movementId: string;
  private firstInit: boolean = true;
  
  // Map
  private map;
  private mapLayer = L.tileLayer('http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=AIzaSyChxd9LP-pHKK1qe1nPlECcK5iJb-a4hg8', {
      maxZoom: 18,
      minZoom: 3,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  private drawLayer = L.layerGroup();

  constructor(
    private browserLocation: Location,
    pageHelperService: PageHelperService,
    private movementService: MovementService,
    applicationStatusService: ApplicationStatusService,
    private roadsService: RoadsService,
    private route: ActivatedRoute,
    private customerSettings: CustomerSettings) { 
      super(pageHelperService, applicationStatusService, customerSettings, "Route Map");
    }

  ngOnInit() {
    this.route.params.subscribe(({ id }) => {
      if (!this.firstInit) {
        this.locationDataLoaded = false;
        this.getData(false);
      }
    });
    this.firstInit = false;
  }  
  
  ngOnDestroy(): void {
    if (this.map ){
      this.map.eachLayer(function(layer){
          layer.remove();
      });
      this.map.remove();
      this.map = null;
      this.mapDiv.nativeElement.innerHTML = "";
    }
  }

  getData(companyChange: boolean) {
    this.movementId = this.route.snapshot.paramMap.get('id');
    this.movementService.getMovementDetails(this.movementId, this.pageHelperService.selectedCompanyId).subscribe(data => 
    {
      this.job = data.job;
      this.jobModel = this.mapDtoToModel(this.job);
      this.routePositions = data.journeyPositions;
      this.initMap();

      this.getSnappedPath(data.job, data.journeyPositions, data.snapToRoadsInformation);
    });
  }

  sendSnappedPointsToServer(json: string) {
    this.movementService.saveMovementDetails({movementId: this.movementId, snapToRoadsInformation: json}).subscribe(response => {
      debugger;
    });
  }

  getSnappedPath(job: JourneyItemDto, positions: any[], snapToRoadsInformation: string) {
    
    // Add the top and from locations
    positions.unshift({
      latitude: job.fromLocationLatitude,
      longitude: job.fromLocationLongitude
    });
    if (job.toLocationLatitude != null) {
      positions.push({
        latitude: job.toLocationLatitude,
        longitude: job.toLocationLongitude
      });
    };

    if (snapToRoadsInformation != '') {
      var points = snapToRoadsInformation
      const pointsArray: GoogleSnappedRoadItem[] = JSON.parse(snapToRoadsInformation) as GoogleSnappedRoadItem[];
      this.drawRoads(pointsArray);
      debugger;
      return;
    }

    // The snaptoroads api can only deal with 100 positions so split into pages
    var pages = this.paginate(positions, 98);
    // Create some overlap in the pages if we have more than one page.
    if (pages.length > 1) {
      pages.forEach((page, index) => {
        // If its the first page or a middle page add the first position from the next page
        if (index == 0 || index != pages.length - 1) {
          page.push(pages[index + 1][0]);
        }

        // If its the last page or a middle page add the last position from the previous page
        if (index == pages.length - 1 || index != 0) {
          page.unshift(pages[index - 1][pages[index - 1].length - 1]);
        }
      });
    }
    
    const mergedRequests: Array<Promise<GoogleSnappedRoadResult>> = [];
    pages.forEach((item) => {
      mergedRequests.push(this.roadsService.getSnapToRoadsPositions(job, item) //returns observable
          .toPromise());
      });
    Promise.all(mergedRequests)
      .then(() => {
        let points: GoogleSnappedRoadItem[] = []; 
        mergedRequests.forEach((request, index) => {
          request.then(data => {
            points = points.concat(data.snappedPoints)

            if (index == mergedRequests.length - 1) {
              const jsonString = JSON.stringify(points);

          // Send the JSON string to the server using an AJAX request
              this.sendSnappedPointsToServer(jsonString);

              this.drawRoads(points)
            }
          });
        });
      });
  }

  private initMap(): void {
    var allLatLons = [];
    var lastPosition = this.routePositions[this.routePositions.length - 1];
    allLatLons.push({ lat: this.job.fromLocationLatitude, lon: this.job.fromLocationLongitude })
    
    if (this.job.toLocationLatitude != null) {
      allLatLons.push({ lat: this.job.toLocationLatitude, lon: this.job.toLocationLongitude })
    }
    this.routePositions.forEach(pos => {
      allLatLons.push({ lat: pos.latitude, lon: pos.longitude })
    })

    var centre = GeoHelper.GetCenterFromPositions(allLatLons);
    
    if (!this.map) {
      let zoom = GeoHelper.CalculateZoom(allLatLons, this.mapDiv.nativeElement.offsetHeight, this.mapDiv.nativeElement.offsetWidth);
      this.map = L.map('map', {
        center: [ centre.lat, centre.lon ],
        zoom: zoom,
        scrollWheelZoom: false
      });

      this.mapLayer.addTo(this.map);
      this.drawLayer.addTo(this.map);
      setTimeout(() => {
        this.map.invalidateSize(true);
      }, 1)
    } else {
      this.map.flyTo(new L.LatLng(centre.lat, centre.lon), 15, {
        animate: true,
        duration: 1.5
      });
    }

    // Clear  and re add draw layer
    this.map.removeLayer(this.drawLayer);
    this.drawLayer = L.layerGroup();
    this.drawLayer.addTo(this.map);
    
    this.addLocationPin(this.job.fromLocationId, this.job.fromLocationTypeName, this.job.fromLocationTypeName, this.job.fromLocationLatitude, this.job.fromLocationLongitude);
    if (this.job.toLocationId != null) {
      this.addLocationPin(this.job.toLocationId, this.job.toLocationTypeName, this.job.toLocationTypeName, this.job.toLocationLatitude, this.job.toLocationLongitude);
    } else {
      this.addLocationPin(null, null, null, lastPosition.latitude, lastPosition.longitude);
    }
  }

  drawRoads(points: GoogleSnappedRoadItem[]) {
    let latlngs = [];
    let fromLocation = [];
    fromLocation.push(this.job.fromLocationLatitude)
    fromLocation.push(this.job.fromLocationLongitude)
    latlngs.push(fromLocation);
    points.forEach(point => {
      let item = [];
      if (point) {
        item.push(point.location.latitude)
        item.push(point.location.longitude)
        latlngs.push(item);
      }
    });
    
    if (this.job.toLocationLatitude != null) {
      let toLocation = [];
      toLocation.push(this.job.toLocationLatitude)
      toLocation.push(this.job.toLocationLongitude)
      latlngs.push(toLocation);
    } else {
      var lastPosition = this.routePositions[this.routePositions.length - 1];
      let vehicleLocation = [];
      vehicleLocation.push(lastPosition.latitude)
      vehicleLocation.push(lastPosition.longitude)
      latlngs.push(vehicleLocation);
    }
    L.polyline(latlngs, {color: 'black'}).addTo(this.drawLayer);
  }

  reset() {

  }

  back(): void {
    this.ngOnDestroy();
    this.browserLocation.back()
  }

  addLocationPin(locationId, locationTypeName, locationName, locationLatitude, locationLongitude) {
    var className = locationTypeName == null ? "vehicle" : LocationClassNameHelper.getLocationClassName(locationTypeName);
    let icon = L.divIcon({
      className: 'custom-div-icon',
      html: "<div class='marker-pin " + className + "'></div><i class='material-icons'>" + (locationTypeName == null ? "local_shipping" : "home") + "</i>",
      iconSize: [30, 42],
      iconAnchor: [15, 36]
    });
    
    L.marker([locationLatitude, locationLongitude], { icon: icon }).addTo(this.drawLayer);
  }

  paginate (arr, size) {
    return arr.reduce((acc, val, i) => {
      let idx = Math.floor(i / size)
      let page = acc[idx] || (acc[idx] = [])
      page.push(val)
  
      return acc
    }, [])
  }

  mapDtoToModel(dto: JourneyItemDto): JobItemModel {
    let model: JobItemModel = dto;
    model.journeyFormatted = DateTimeHelper.formatTimespan(model.journeyTimeMinutes);
    model.arrivedAtFormatted = DateTimeHelper.formatDateAndTime(model.arrivedAt);
    model.departedAtFormatted = DateTimeHelper.formatDateAndTime(model.departedAt);
    if (model.mileage) {
      model.mileageFormatted =  (Math.round(model.mileage * 10) / 10).toString();
    }
    model.mileageVarianceFormatted = (Math.round(model.mileageVariance * 10) / 10).toString();
    return model;
  }
}
